import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from '../config/api';
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  FileText,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Search,
  ChevronLeft,
  AlertCircle,
  Send,
  Loader2,
  Upload,
} from "lucide-react";
import { fmtUK } from "../lib/dateUtils";
import { toast } from "sonner";
import MatterSearchUnavailableNotice from "../components/MatterSearchUnavailableNotice";
// Aliased: OutgoingPostDetail already has a local searchMatters callback, and an
// import that shadows or is shadowed by a local name is how you get silent
// recursion. The alias makes the call site unambiguous in every file.
import { searchMatters as searchMattersApi, MatterSearchUnavailable, MATTER_SEARCH_UNAVAILABLE_MESSAGE } from "../lib/matterSearch";

const OUTGOING_DOC_TYPES = [
  { value: "letter", label: "Letter" },
  { value: "report", label: "Report" },
  { value: "contract", label: "Contract" },
  { value: "deed", label: "Deed" },
  { value: "completion_statement", label: "Completion Statement" },
  { value: "other", label: "Other" },
];

export default function OutgoingPostQueue() {
  const navigate = useNavigate();

  // Scanned outgoing items (from SharePoint OUTGOING POST folder)
  const [scannedItems, setScannedItems] = useState([]);
  const [scannedLoading, setScannedLoading] = useState(false);
  const [scannedCounts, setScannedCounts] = useState({ pending: 0, filed: 0 });
  const [outSyncing, setOutSyncing] = useState(false);

  // Inline matter assignment for pending scanned items
  const [assigningId, setAssigningId] = useState(null);
  const [assignMatterSearch, setAssignMatterSearch] = useState("");
  const [assignMatterResults, setAssignMatterResults] = useState([]);
  const [assignSearching, setAssignSearching] = useState(false);
  // BM-H285: an unreadable matter list gets its own state, so the UI can say
  // "we could not look" rather than showing an empty dropdown.
  const [assignMatterError, setAssignMatterError] = useState("");
  const assignSearchTimeout = useRef(null);

  // Manual upload form
  const [showManualUpload, setShowManualUpload] = useState(false);
  const [outMatterSearch, setOutMatterSearch] = useState("");
  const [outMatterResults, setOutMatterResults] = useState([]);
  const [outMatterRef, setOutMatterRef] = useState("");
  const [outDocType, setOutDocType] = useState("");
  const [outDescription, setOutDescription] = useState("");
  const [outDateSent, setOutDateSent] = useState(new Date().toISOString().slice(0, 10));
  const [outFile, setOutFile] = useState(null);
  const [outUploading, setOutUploading] = useState(false);
  const [outSearching, setOutSearching] = useState(false);
  const [outMatterError, setOutMatterError] = useState("");
  const fileInputRef = useRef(null);
  const matterSearchTimeout = useRef(null);

  // Recent manual uploads
  const [outRecentItems, setOutRecentItems] = useState([]);
  const [outLoadingRecent, setOutLoadingRecent] = useState(false);

  // Search/filter for filed items
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");

  const fetchScannedOutgoing = useCallback(async () => {
    setScannedLoading(true);
    try {
      const res = await axios.get(`${API}/postintake/outgoing/scanned?limit=50`);
      setScannedItems(res.data.items || []);
      setScannedCounts({ pending: res.data.pending || 0, filed: res.data.filed || 0 });
    } catch {
      toast.error("Failed to load outgoing items");
    } finally {
      setScannedLoading(false);
    }
  }, []);

  const fetchOutgoingRecent = useCallback(async () => {
    setOutLoadingRecent(true);
    try {
      const res = await axios.get(`${API}/postintake/outgoing?limit=20`);
      setOutRecentItems(res.data.items || []);
    } catch {
      // non-critical
    } finally {
      setOutLoadingRecent(false);
    }
  }, []);

  useEffect(() => {
    fetchScannedOutgoing();
    fetchOutgoingRecent();
  }, [fetchScannedOutgoing, fetchOutgoingRecent]);

  const handleOutgoingSync = async () => {
    setOutSyncing(true);
    try {
      const res = await axios.post(`${API}/postintake/outgoing/sync`);
      toast.success(`Outgoing sync complete — ${res.data.pending} pending, ${res.data.filed} filed`);
      fetchScannedOutgoing();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to sync outgoing post"));
    } finally {
      setOutSyncing(false);
    }
  };

  const handleAssignMatterSearch = (query) => {
    setAssignMatterSearch(query);
    if (assignSearchTimeout.current) clearTimeout(assignSearchTimeout.current);
    if (!query.trim()) { setAssignMatterResults([]); return; }
    assignSearchTimeout.current = setTimeout(async () => {
      setAssignSearching(true);
      try {
        // BM-H285: searchMatters throws rather than resolving empty when the
        // matter list could not be read, so a broken search can no longer
        // masquerade as a matter that does not exist.
        const results = await searchMattersApi(query);
        setAssignMatterError("");
        setAssignMatterResults(results);
      } catch (err) {
        setAssignMatterResults([]);
        setAssignMatterError(
          err instanceof MatterSearchUnavailable
            ? err.message
            : MATTER_SEARCH_UNAVAILABLE_MESSAGE
        );
        toast.error("Matter search unavailable. Do not file on an empty result.");
      } finally { setAssignSearching(false); }
    }, 300);
  };

  const handleAssignToMatter = async (itemId, matterRef) => {
    try {
      await axios.post(`${API}/postintake/outgoing/${itemId}/assign`, {
        matter_ref: matterRef,
        assigned_by: "",
      });
      toast.success(`Filed to ${matterRef}`);
      setAssigningId(null);
      setAssignMatterSearch("");
      setAssignMatterResults([]);
      fetchScannedOutgoing();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to assign to matter"));
    }
  };

  const handleMatterSearch = (query) => {
    setOutMatterSearch(query);
    setOutMatterRef("");
    if (matterSearchTimeout.current) clearTimeout(matterSearchTimeout.current);
    if (!query.trim()) { setOutMatterResults([]); return; }
    matterSearchTimeout.current = setTimeout(async () => {
      setOutSearching(true);
      try {
        // BM-H285: searchMatters throws rather than resolving empty when the
        // matter list could not be read, so a broken search can no longer
        // masquerade as a matter that does not exist.
        const results = await searchMattersApi(query);
        setOutMatterError("");
        setOutMatterResults(results);
      } catch (err) {
        setOutMatterResults([]);
        setOutMatterError(
          err instanceof MatterSearchUnavailable
            ? err.message
            : MATTER_SEARCH_UNAVAILABLE_MESSAGE
        );
        toast.error("Matter search unavailable. Do not file on an empty result.");
      } finally { setOutSearching(false); }
    }, 300);
  };

  const selectMatter = (folder) => {
    setOutMatterRef(folder.name);
    setOutMatterSearch(folder.name);
    setOutMatterResults([]);
  };

  const handleOutgoingSubmit = async () => {
    const matterRef = outMatterRef || outMatterSearch.trim();
    if (!matterRef) { toast.error("Please select or enter a matter reference"); return; }
    if (!outDocType) { toast.error("Please select a document type"); return; }
    if (!outFile) { toast.error("Please select a file to upload"); return; }

    setOutUploading(true);
    try {
      const formData = new FormData();
      formData.append("matter_ref", matterRef);
      formData.append("document_type", outDocType);
      formData.append("date_sent", outDateSent);
      formData.append("description", outDescription.trim());
      formData.append("file", outFile);
      formData.append("uploaded_by", "");

      const res = await axios.post(`${API}/postintake/outgoing`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(res.data.sharepoint_url
        ? "Document uploaded to SharePoint"
        : "Document recorded (SharePoint upload pending)"
      );

      setOutMatterSearch(""); setOutMatterRef(""); setOutDocType("");
      setOutDescription(""); setOutDateSent(new Date().toISOString().slice(0, 10));
      setOutFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchOutgoingRecent();
    } catch (err) {
      toast.error(errorMessage(err, "Failed to upload document"));
    } finally {
      setOutUploading(false);
    }
  };

  const formatDate = (dateStr) => fmtUK(dateStr, "dd MMM yyyy HH:mm");

  const filteredScanned = scannedItems.filter(item => {
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    if (!searchTerm) return matchesStatus;
    const s = searchTerm.toLowerCase();
    return matchesStatus && (
      item.filename?.toLowerCase().includes(s) ||
      item.matter_ref?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      {/* Header */}
      <div className="glass-nav sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="gap-1">
                <ChevronLeft className="w-4 h-4" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-bold text-teal flex items-center gap-2">
                  <Send className="w-6 h-6" />
                  Outgoing Post
                </h1>
                <p className="text-sm text-slate-500">Manage scanned and uploaded outgoing documents</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowManualUpload(v => !v)}
                className="gap-1"
                data-testid="toggle-manual-upload"
              >
                <Upload className="w-4 h-4" />
                {showManualUpload ? "Hide Upload" : "Upload Manually"}
              </Button>
              <Button
                onClick={handleOutgoingSync}
                disabled={outSyncing}
                className="gap-2 bg-teal hover:bg-teal/90"
                data-testid="outgoing-sync-btn"
              >
                <RefreshCw className={`w-4 h-4 ${outSyncing ? "animate-spin" : ""}`} />
                {outSyncing ? "Scanning..." : "Sync from SharePoint"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          <button
            onClick={() => setStatusFilter("pending")}
            className={`p-4 rounded-md border transition-colors ${
              statusFilter === "pending"
                ? "bg-status-amber-dim border-amber-300"
                : "bg-white border-navy-border hover:border-amber-200"
            }`}
            data-testid="filter-pending"
          >
            <p className="text-2xl font-bold text-status-amber">{scannedCounts.pending}</p>
            <p className="text-sm text-slate-500">Pending Assignment</p>
          </button>
          <button
            onClick={() => setStatusFilter("filed")}
            className={`p-4 rounded-md border transition-colors ${
              statusFilter === "filed"
                ? "bg-teal-dim border-teal"
                : "bg-white border-navy-border hover:border-teal"
            }`}
            data-testid="filter-filed"
          >
            <p className="text-2xl font-bold text-teal">{scannedCounts.filed}</p>
            <p className="text-sm text-slate-500">Filed</p>
          </button>
          <button
            onClick={() => setStatusFilter("all")}
            className={`p-4 rounded-md border transition-colors ${
              statusFilter === "all"
                ? "bg-slate-100 border-slate-400"
                : "bg-white border-navy-border hover:border-slate-300"
            }`}
            data-testid="filter-all"
          >
            <p className="text-2xl font-bold text-txt-muted">{scannedItems.length}</p>
            <p className="text-sm text-slate-500">Total</p>
          </button>
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by filename or matter..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="filed">Filed</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => { fetchScannedOutgoing(); fetchOutgoingRecent(); }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>

        {/* Scanned Items List */}
        {scannedLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block w-8 h-8 border-2 border-teal border-t-transparent rounded-md animate-spin" />
            <p className="mt-4 text-slate-500">Loading outgoing post...</p>
          </div>
        ) : filteredScanned.length === 0 ? (
          <div className="p-12 text-center bg-white rounded-md border border-dashed border-slate-300">
            <Send className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No outgoing documents found</p>
            <p className="text-sm text-slate-400 mt-1">
              Click "Sync from SharePoint" to check for new scanned files, or upload manually
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredScanned.map((item) => (
              <div
                key={item.id}
                className={`bg-white border rounded-md p-4 ${
                  item.status === "pending"
                    ? "border-amber-200 hover:border-amber-300"
                    : "border-navy-border hover:border-teal/50"
                } transition-colors cursor-pointer`}
                onClick={() => navigate(`/outgoing-post/${item.id}`)}
                data-testid={`outgoing-item-${item.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                      item.status === "pending" ? "bg-status-amber-dim" : "bg-teal-dim"
                    }`}>
                      {item.status === "filed"
                        ? <CheckCircle2 className="w-5 h-5 text-teal" />
                        : <FileText className="w-5 h-5 text-status-amber" />
                      }
                    </div>
                    <div>
                      <p className="font-medium text-white">{item.filename}</p>
                      <p className="text-sm text-slate-500">
                        {item.matter_ref ? `${item.matter_ref} · ` : ""}
                        Scanned: {formatDate(item.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {item.status === "pending" ? (
                      <Badge variant="outline" className="bg-status-amber-dim text-status-amber border-amber-200 gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Needs Assignment
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-teal-dim text-teal border-teal gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Filed
                      </Badge>
                    )}

                    {/* Inline assign for pending items */}
                    {item.status === "pending" && (
                      assigningId === item.id ? (
                        <div className="relative w-64" onClick={e => e.stopPropagation()}>
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                              autoFocus
                              value={assignMatterSearch}
                              onChange={(e) => handleAssignMatterSearch(e.target.value)}
                              placeholder="Search matter..."
                              className="pl-10 h-8 text-sm"
                              data-testid={`assign-search-${item.id}`}
                            />
                            {assignSearching && (
                              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 animate-spin" />
                            )}
                          </div>
                          <MatterSearchUnavailableNotice message={assignMatterError} />
                          {assignMatterResults.length > 0 && (
                            <div className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg max-h-40 overflow-y-auto">
                              {assignMatterResults.map((folder) => (
                                <button
                                  key={folder.id}
                                  onClick={() => handleAssignToMatter(item.id, folder.name)}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-[color:var(--surface-inset)] transition-colors border-b border-slate-100 last:border-0"
                                >
                                  <span className="font-medium">{folder.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            className="absolute -right-8 top-0 h-8 w-8 p-0"
                            onClick={() => { setAssigningId(null); setAssignMatterSearch(""); setAssignMatterResults([]); }}
                          >
                            <XCircle className="w-4 h-4 text-slate-400" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm" variant="outline"
                          className="gap-1 border-teal text-teal hover:bg-teal/10"
                          onClick={(e) => { e.stopPropagation(); setAssigningId(item.id); setAssignMatterSearch(""); setAssignMatterResults([]); }}
                          data-testid={`assign-btn-${item.id}`}
                        >
                          <Search className="w-3 h-3" />
                          Assign to matter
                        </Button>
                      )
                    )}

                    {item.filed_sharepoint_url && (
                      <a
                        href={item.filed_sharepoint_url}
                        target="_blank" rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="text-xs text-teal hover:underline"
                      >
                        Open
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Manual Upload Form */}
        {showManualUpload && (
          <div className="bg-white border border-navy-border rounded-md p-6">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal" />
              Upload Manually
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Matter Search */}
              <div className="relative">
                <label className="block text-sm font-medium text-txt mb-1">
                  Matter Reference <span className="text-status-red">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={outMatterSearch}
                    onChange={(e) => handleMatterSearch(e.target.value)}
                    placeholder="Search for a matter..."
                    className="pl-10"
                    data-testid="outgoing-matter-search"
                  />
                  {outSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                  )}
                </div>
                <MatterSearchUnavailableNotice message={outMatterError} />
                {outMatterResults.length > 0 && !outMatterRef && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-navy-border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {outMatterResults.map((folder) => (
                      <button
                        key={folder.id}
                        onClick={() => selectMatter(folder)}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-[color:var(--surface-inset)] transition-colors border-b border-slate-100 last:border-0"
                        data-testid={`outgoing-matter-option-${folder.id}`}
                      >
                        <span className="font-medium">{folder.name}</span>
                        <span className="text-slate-400 ml-2 text-xs">{folder.childCount} items</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Document Type */}
              <div>
                <label className="block text-sm font-medium text-txt mb-1">
                  Document Type <span className="text-status-red">*</span>
                </label>
                <Select value={outDocType} onValueChange={setOutDocType}>
                  <SelectTrigger data-testid="outgoing-doc-type">
                    <SelectValue placeholder="Select type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {OUTGOING_DOC_TYPES.map((dt) => (
                      <SelectItem key={dt.value} value={dt.value}>{dt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-txt mb-1">
                  Description <span className="text-slate-400 text-xs">(optional)</span>
                </label>
                <Input
                  value={outDescription}
                  onChange={(e) => setOutDescription(e.target.value)}
                  placeholder="e.g. Letter to Land Registry re Title"
                  data-testid="outgoing-description"
                />
              </div>

              {/* Date Sent */}
              <div>
                <label className="block text-sm font-medium text-txt mb-1">Date Sent</label>
                <Input
                  type="date"
                  value={outDateSent}
                  onChange={(e) => setOutDateSent(e.target.value)}
                  data-testid="outgoing-date-sent"
                />
              </div>

              {/* File Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-txt mb-1">
                  File <span className="text-status-red">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                  onChange={(e) => setOutFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-teal/10 file:text-teal hover:file:bg-teal/20"
                  data-testid="outgoing-file-input"
                />
                {outFile && (
                  <p className="text-xs text-slate-400 mt-1">{outFile.name} ({(outFile.size / 1024).toFixed(0)} KB)</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={handleOutgoingSubmit}
                disabled={outUploading || !outFile || !outDocType || !(outMatterRef || outMatterSearch.trim())}
                className="gap-2 bg-teal hover:bg-teal/90"
                data-testid="outgoing-submit"
              >
                {outUploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Uploading...</>
                ) : (
                  <><Upload className="w-4 h-4" />Upload to SharePoint</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Recent Manual Uploads */}
        {outRecentItems.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-txt-muted uppercase tracking-wider mb-3">
              Recent Manual Uploads
            </h3>
            <div className="space-y-2">
              {outRecentItems.map((item) => (
                <div
                  key={item.id}
                  className="bg-white border border-navy-border rounded-md p-4"
                  data-testid={`manual-upload-item-${item.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-50 rounded-md flex items-center justify-center">
                        <Send className="w-5 h-5 text-blue-500" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{item.original_filename}</p>
                        <p className="text-sm text-slate-500">
                          {item.matter_ref} · {OUTGOING_DOC_TYPES.find(t => t.value === item.document_type)?.label || item.document_type}
                          {item.description ? ` — ${item.description}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400">Sent: {item.date_sent}</span>
                      {item.sharepoint_url ? (
                        <Badge variant="outline" className="bg-teal-dim text-teal border-teal gap-1">
                          <CheckCircle2 className="w-3 h-3" />Filed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-status-amber-dim text-status-amber border-amber-200 gap-1">
                          <AlertCircle className="w-3 h-3" />Pending
                        </Badge>
                      )}
                      {item.sharepoint_url && (
                        <a href={item.sharepoint_url} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-teal hover:underline">Open</a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
